import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Helmet } from "react-helmet-async";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Link } from "react-router-dom";
import { ArrowRight, Wrench, Sparkles, Zap } from "lucide-react";
import heroBackground from "@/assets/hero-background.jpg";

const Services = () => {
  const services = [
    {
      icon: Wrench,
      title: "Tank Fabrication",
      description: "Custom stainless steel tank fabrication with complete EPCC solutions",
      features: [
        "Shop-fabricated or site-erected options",
        "SS304, SS316L, Duplex materials",
        "Custom designs and engineering",
        "Complete installation services"
      ],
      link: "/services/tank-fabrication"
    },
    {
      icon: Sparkles,
      title: "Mechanical Polishing",
      description: "Advanced surface finishing with mirror-quality results",
      features: [
        "Semi-auto wide sand belt polishing",
        "Max plate: 1524mm × 9144mm",
        "Dish-end polishing up to 4000mm",
        "Surface roughness Ra 0.1 μm"
      ],
      link: "/services/mechanical-polishing"
    },
    {
      icon: Zap,
      title: "Electropolishing",
      description: "Southeast Asia's largest EP facility for premium surface finishing",
      features: [
        "Tank size: 1200×3000×1200mm",
        "Custom jigs for any tank size",
        "Pharma & biotech grade finishing",
        "MITUTOYO certified testing"
      ],
      link: "/services/electropolishing"
    }
  ];

  return (
    <div className="min-h-screen bg-page-modern">
      <Helmet>
        <title>Services | Tank Fabrication, Polishing & EPCC Solutions - WLE Technology</title>
        <meta name="description" content="Comprehensive tank fabrication services including custom stainless steel tanks, mechanical polishing, electropolishing, and complete EPCC solutions in Malaysia." />
        <link rel="canonical" href="https://wletec.com/services" />
        <meta property="og:title" content="WLE Technology Services - Tank Fabrication & Finishing" />
        <meta property="og:description" content="Complete tank fabrication and finishing services from design to delivery." />
        <meta property="og:url" content="https://wletec.com/services" />
        <meta property="og:type" content="website" />
        <meta name="keywords" content="tank fabrication services, mechanical polishing, electropolishing, EPCC solutions, Malaysia" />
      </Helmet>
      <Navigation />
      
      {/* Hero Section */}
      <section 
        className="py-24 relative"
        style={{
          backgroundImage: `url(${heroBackground})`,
          backgroundSize: 'cover',
          backgroundPosition: 'top',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-background/95 via-background/90 to-background/80" />
        <div className="absolute inset-0 bg-grid-pattern opacity-30" />
        <div className="container relative z-10">
          <div className="text-center mb-16">
            <h1 className="text-5xl font-bold text-foreground mb-6">
              Our <span className="text-wle-blue">Services</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Complete tank fabrication and finishing services. From initial design to final delivery, 
              we handle every aspect of your project with precision and expertise.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 bg-subtle-texture">
        <div className="container">
          <div className="grid lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <Card key={index} className="group hover:shadow-elegant transition-all duration-300 h-full">
                <CardHeader className="text-center pb-6">
                  <div className="mx-auto mb-4 p-4 rounded-full bg-wle-blue/10">
                    <service.icon className="h-8 w-8 text-wle-blue" />
                  </div>
                  <CardTitle className="text-2xl mb-2">{service.title}</CardTitle>
                  <CardDescription className="text-base">{service.description}</CardDescription>
                </CardHeader>
                <CardContent className="flex-1 flex flex-col">
                  <ul className="space-y-3 mb-8 flex-1">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <div className="h-1.5 w-1.5 rounded-full bg-wle-blue mt-2 flex-shrink-0" />
                        <span className="text-sm text-muted-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button asChild className="w-full">
                    <Link to={service.link}>
                      Learn More
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 bg-grid-pattern">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-foreground mb-4">Our EPCC Process</h2>
            <p className="text-muted-foreground">Complete Engineering, Procurement, Construction & Commissioning</p>
          </div>
          
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { step: "01", title: "Engineering", desc: "Custom design & drawings" },
              { step: "02", title: "Procurement", desc: "Material sourcing & quality" },
              { step: "03", title: "Construction", desc: "Fabrication & assembly" },
              { step: "04", title: "Commissioning", desc: "Installation & testing" }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-gradient-primary flex items-center justify-center text-white font-bold text-lg">
                  {item.step}
                </div>
                <h3 className="font-semibold mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Services;